import type {
  ConvocarInput,
  ConvocacaoInvite,
  ListConvocacoesQuery,
  ListConvocacoesResponse,
  RemainingRateLimit,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * Phase 26 — CONVOCAR direto (REQ-CATALOGO-01).
 *
 * Empresa surface:
 *   convocar(eventoId, input)      POST /empresa/eventos/:id/convocar
 *   listarEnviadas(query)          GET  /empresa/convocacoes
 *   getLimites()                   GET  /empresa/convocacoes/limites
 *
 * Profissional surface:
 *   listarRecebidas(query)         GET  /convocacoes
 *   aceitar(inviteId)              POST /convocacoes/:id/aceitar
 *   rejeitar(inviteId)             POST /convocacoes/:id/rejeitar
 */
export class ConvocacoesEndpoints {
  constructor(private readonly client: ApiClient) {}

  convocar(
    eventoId: string,
    input: ConvocarInput,
  ): Promise<ConvocacaoInvite> {
    return this.client.post<ConvocacaoInvite>(
      `/empresa/eventos/${eventoId}/convocar`,
      input,
    );
  }

  cancelar(
    inviteId: string,
  ): Promise<{ id: string; status: 'CANCELADO' }> {
    return this.client.post<{ id: string; status: 'CANCELADO' }>(
      `/empresa/convocacoes/${inviteId}/cancelar`,
      {},
    );
  }

  aceitar(
    inviteId: string,
  ): Promise<{ inviteId: string; candidaturaId: string }> {
    return this.client.post<{ inviteId: string; candidaturaId: string }>(
      `/convocacoes/${inviteId}/aceitar`,
      {},
    );
  }

  rejeitar(
    inviteId: string,
  ): Promise<{ inviteId: string; status: string }> {
    return this.client.post<{ inviteId: string; status: string }>(
      `/convocacoes/${inviteId}/rejeitar`,
      {},
    );
  }

  listarRecebidas(
    query?: Partial<ListConvocacoesQuery>,
  ): Promise<ListConvocacoesResponse> {
    return this.client.get<ListConvocacoesResponse>('/convocacoes', {
      query: this.toQuery(query),
    });
  }

  listarEnviadas(
    query?: Partial<ListConvocacoesQuery>,
  ): Promise<ListConvocacoesResponse> {
    return this.client.get<ListConvocacoesResponse>('/empresa/convocacoes', {
      query: this.toQuery(query),
    });
  }

  getLimites(): Promise<RemainingRateLimit> {
    return this.client.get<RemainingRateLimit>('/empresa/convocacoes/limites');
  }

  private toQuery(
    q?: Partial<ListConvocacoesQuery>,
  ): Record<string, string | number | undefined> {
    if (!q) return {};
    const out: Record<string, string | number | undefined> = {};
    if (q.status) out['status'] = q.status;
    if (q.page !== undefined) out['page'] = q.page;
    if (q.limit !== undefined) out['limit'] = q.limit;
    return out;
  }
}
